import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Heart, Trophy, TrendingUp, TrendingDown, Gift } from "lucide-react";
import { api } from "../lib/api";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const resolveUrl = (url) => (url?.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

function RankNotification({ n }) {
  const rival = n.rival_display || "another collector";
  const config = {
    rank_up: {
      icon: TrendingUp,
      accent: "text-emerald-300",
      border: "border-emerald-400/20 bg-emerald-500/5 hover:border-emerald-400/40",
      message: (
        <>You overtook <span className="font-semibold text-emerald-300">{rival}</span> — you're now{" "}
          <span className="font-semibold text-white">#{n.rank}</span> on the collector leaderboard!</>
      ),
    },
    rank_down: {
      icon: TrendingDown,
      accent: "text-red-300",
      border: "border-red-400/20 bg-red-500/5 hover:border-red-400/40",
      message: (
        <><span className="font-semibold text-red-300">{rival}</span> just overtook you — you've slipped to{" "}
          <span className="font-semibold text-white">#{n.rank}</span>. Scan more cards to reclaim your spot!</>
      ),
    },
    rank_enter: {
      icon: Trophy,
      accent: "text-[#F59E0B]",
      border: "border-[#F59E0B]/20 bg-[#F59E0B]/5 hover:border-[#F59E0B]/40",
      message: (
        <>You broke into the leaderboard <span className="font-semibold text-[#F59E0B]">Top 10</span> at{" "}
          <span className="font-semibold text-white">#{n.rank}</span>!</>
      ),
    },
  }[n.type];
  const Icon = config.icon;
  return (
    <Link
      to="/community"
      data-testid={`notification-item-${n.id}`}
      className={`flex items-center gap-3 rounded-lg p-2 border transition-colors ${config.border} ${n.read ? "opacity-70" : ""}`}
    >
      <span className={`w-9 h-9 shrink-0 rounded-full border border-white/10 bg-black/40 flex items-center justify-center ${config.accent}`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <p className="text-xs leading-snug">{config.message}</p>
        <p className="text-[10px] text-zinc-600 mt-0.5">{new Date(n.created_at).toLocaleString()} · tap to see the leaderboard</p>
      </div>
    </Link>
  );
}

export default function NotificationsBell() {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState(null);

  useEffect(() => {
    const fetchCount = () =>
      api.get("/notifications/unread-count").then((res) => setUnread(res.data.count)).catch(() => {});
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenChange = async (open) => {
    if (!open) return;
    try {
      const res = await api.get("/notifications");
      setItems(res.data);
      if (unread > 0) {
        await api.post("/notifications/mark-read");
        setUnread(0);
      }
    } catch {
      setItems([]);
    }
  };

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="navbar-notifications-bell"
          title="Alerts"
          className="relative text-zinc-400 hover:text-white transition-colors"
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span data-testid="navbar-notifications-badge" className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-pink-500 text-white text-[10px] font-bold flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid="notifications-popover" align="end" className="w-80 bg-[#111113] border-white/10 text-white p-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
          <Bell className="w-3 h-3 text-[#A78BFA]" /> Alerts
        </p>
        {items === null ? (
          <p className="text-xs text-zinc-600 py-4 text-center animate-pulse">Loading...</p>
        ) : items.length === 0 ? (
          <p data-testid="notifications-empty" className="text-xs text-zinc-600 py-4 text-center">
            No alerts yet — Chase Deck matches and leaderboard shake-ups will land here.
          </p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {items.map((n) =>
              n.type === "rank_up" || n.type === "rank_down" || n.type === "rank_enter" ? (
                <RankNotification key={n.id} n={n} />
              ) : n.type === "referral" ? (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  className={`flex items-center gap-3 rounded-lg p-2 border border-emerald-400/20 bg-emerald-500/5 ${n.read ? "opacity-70" : ""}`}
                >
                  <span className="w-9 h-9 shrink-0 rounded-full border border-white/10 bg-black/40 flex items-center justify-center text-emerald-300">
                    <Gift className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs leading-snug">
                      <span className="font-semibold text-emerald-300">{n.friend_name || "A collector"}</span> joined CardArk with your invite —{" "}
                      <span className="font-semibold text-white">+{n.credits} credits</span> added!
                    </p>
                    <p className="text-[10px] text-zinc-600 mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ) : (
                <Link
                  key={n.id}
                  to={`/collector/${n.owner_user_id}`}
                  data-testid={`notification-item-${n.id}`}
                  className={`flex items-center gap-3 rounded-lg p-2 border transition-colors hover:border-pink-400/40 ${n.read ? "border-white/5 opacity-70" : "border-pink-400/20 bg-pink-500/5"}`}
                >
                  {n.card_image ? (
                    <img src={pcImageUrl(resolveUrl(n.card_image), "240")} onError={pcImageFallback} alt={n.card_name} className="w-9 aspect-[3/4] object-cover rounded border border-white/10 shrink-0" />
                  ) : (
                    <div className="w-9 aspect-[3/4] rounded border border-white/10 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-xs leading-snug">
                      <span className="font-semibold text-pink-300 inline-flex items-center gap-1"><Heart className="w-3 h-3 fill-pink-400 text-pink-400" />{n.card_name}</span> from your Chase Deck was added by{" "}
                      <span className="text-[#A78BFA]">{n.owner_display}</span>
                    </p>
                    <p className="text-[10px] text-zinc-600 mt-0.5">{new Date(n.created_at).toLocaleString()} · tap to view their binder</p>
                  </div>
                </Link>
              )
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
